// IMPORTANTE: usamos la variante con estilos
import * as XLSX from "xlsx-js-style";

const toUpper = (s) => (s || "").toString().trim().toUpperCase();
const safeArr = (v) => (Array.isArray(v) ? v : []);
const n = (v) => Number(v ?? 0);
const sum = (arr) => arr.reduce((a, b) => a + (isFinite(b) ? b : 0), 0);

// Estilos reutilizables
const BORDER_THIN = {
  top: { style: "thin", color: { rgb: "FFBFBFBF" } },
  left: { style: "thin", color: { rgb: "FFBFBFBF" } },
  bottom: { style: "thin", color: { rgb: "FFBFBFBF" } },
  right: { style: "thin", color: { rgb: "FFBFBFBF" } },
};

const HEADER_STYLE = {
  font: { bold: true },
  alignment: { horizontal: "center", vertical: "center", wrapText: true },
  fill: { fgColor: { rgb: "FFEFEFEF" } }, // gris claro
  border: BORDER_THIN,
};

const SUBHEADER_STYLE = {
  font: { bold: true },
  alignment: { horizontal: "center", vertical: "center" },
  fill: { fgColor: { rgb: "FFF5F5F5" } },
  border: BORDER_THIN,
};

const CELL_STYLE = {
  alignment: { vertical: "center" },
  border: BORDER_THIN,
};

const NUMBER_STYLE = {
  ...CELL_STYLE,
  numFmt: "0.00", // SIEMPRE dos decimales
};

const exportSHEETToExcel = (data, fileName) => {
  const items = safeArr(data);
  const first = items[0] || {};

  // Tomamos nombres y orden exactos desde la primera ficha
  const LIQ_HEADERS = safeArr(first.productliquidLinesheet)
    .map((x) => (x?.productliquid_namees || "").toString().trim())
    .slice(0, 8);

  const PROD_HEADERS = safeArr(first.productLinesheet)
    .map((x) => (x?.product_namees || "").toString().trim())
    .slice(0, 40);

  // Cabeceras en dos filas
  const baseHeaders = [
    "Nº FICHA",
    "FECHA DE ENTRADA",
    "NÚMERO DE ORDEN",
    "MARCA",
    "MODELO",
    "MATRÍCULA",
    "BASTIDOR",
    "ESTADO DEL VEHÍCULO",
  ];

  // Fila 1
  const headerRow1 = [
    ...baseHeaders,
    ...LIQ_HEADERS,
    ...PROD_HEADERS.flatMap((h) => [h, "", "", ""]),
  ];

  // Fila 2
  const headerRow2 = [
    ...Array(baseHeaders.length).fill(""),
    ...Array(LIQ_HEADERS.length).fill("CANTIDAD"),
    ...PROD_HEADERS.flatMap(() => ["PESO", "NO", "REPUESTOS", "RESIDUO"]),
  ];

  const formatDate = (dateValue) => {
    if (!dateValue) return "";
    const d = new Date(dateValue);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  // Filas de datos
  const rows = items.map((sheet) => {
    const base = [
      sheet?.ownsheet_id ?? "",
      formatDate(sheet?.date_sheet) ?? "",
      sheet?.num_sheet ?? "",
       sheet?.vehicle_marca ?? "",
      sheet?.vehicle_model ?? "",
      sheet?.vehicle_sheet ?? "",
      sheet?.frame_sheet ?? "",
      sheet?.sheetStatus_namees ?? "",
    ];

    const pll = safeArr(sheet?.productliquidLinesheet);
    const pl = safeArr(sheet?.productLinesheet);

    // Líquidos: cantidad por etiqueta exacta
    const liqCells = LIQ_HEADERS.map((label) => {
      const up = toUpper(label);
      const qty = sum(
        pll.filter((x) => toUpper(x?.productliquid_namees) === up).map((x) => n(x?.amount))
      );
      // devolver número (no string), redondeado a 2 decimales
      return Math.round(qty * 100) / 100;
    });

    // Productos: 4 subcolumnas por producto (PESO, NO, REPUESTOS, RESIDUO)
    const prodCells = PROD_HEADERS.flatMap((label) => {
      const up = toUpper(label);
      const items = pl.filter((x) => toUpper(x?.product_namees) === up);

      const peso = sum(items.map((x) => n(x?.amountWeight)));
      const no = sum(items.map((x) => n(x?.amountNo)));
      const rep = sum(items.map((x) => n(x?.amountSpare)));
      const res = sum(items.map((x) => n(x?.amountResidue)));

      const round2 = (v) => Math.round(v * 100) / 100;
      return [round2(peso), round2(no), round2(rep), round2(res)];
    });

    return [...base, ...liqCells, ...prodCells];
  });

  // Construir hoja
  const ws = XLSX.utils.aoa_to_sheet([headerRow1, headerRow2, ...rows]);

  // Merges: base vertical, productos horizontales
  const merges = [];
  for (let c = 0; c < baseHeaders.length; c++) {
    merges.push({ s: { r: 0, c }, e: { r: 1, c } });
  }
  const startColProducts = baseHeaders.length + LIQ_HEADERS.length;
  for (let i = 0; i < PROD_HEADERS.length; i++) {
    const c0 = startColProducts + i * 4;
    merges.push({ s: { r: 0, c: c0 }, e: { r: 0, c: c0 + 3 } });
  }
  ws["!merges"] = merges;

  // Aplicar estilos a encabezados (fila 1 y 2)
  const totalCols = baseHeaders.length + LIQ_HEADERS.length + PROD_HEADERS.length * 4;
  for (let c = 0; c < totalCols; c++) {
    const addr1 = XLSX.utils.encode_cell({ r: 0, c });
    const addr2 = XLSX.utils.encode_cell({ r: 1, c });
    ws[addr1] = ws[addr1] || { t: "s", v: "" };
    ws[addr2] = ws[addr2] || { t: "s", v: "" };
    // Fila 1
    ws[addr1].s = HEADER_STYLE;
    // Fila 2: subencabezados
    if (c < baseHeaders.length) {
      ws[addr2].s = HEADER_STYLE; // celdas base fusionadas verticalmente
    } else if (c < baseHeaders.length + LIQ_HEADERS.length) {
      ws[addr2].s = SUBHEADER_STYLE; // "CANTIDAD"
    } else {
      ws[addr2].s = SUBHEADER_STYLE; // PESO/NO/REPUESTOS/RESIDUO
    }
  }

  // Formateo de datos: bordes finos y 2 decimales en numéricos (líquidos y productos)
  const startDataRow = 2; // datos comienzan en la tercera fila (0-based)
  const wsRange = XLSX.utils.decode_range(ws["!ref"]);
  for (let r = startDataRow; r <= wsRange.e.r; r++) {
    for (let c = 0; c < totalCols; c++) {
      const addr = XLSX.utils.encode_cell({ r, c });
      const cell = ws[addr];
      if (!cell) continue;
      // Estilo base
      cell.s = cell.s ? { ...CELL_STYLE, ...cell.s } : { ...CELL_STYLE };
      // Numeros con dos decimales a partir de la primera columna de líquidos
      const firstNumericCol = baseHeaders.length; // desde líquidos en adelante son numéricos
      if (c >= firstNumericCol) {
        // asegúrate de que sea numérico y aplícale formato
        const val = Number(cell.v);
        if (!isNaN(val)) {
          cell.t = "n";
          cell.v = Math.round(val * 100) / 100;
          cell.z = "0.00";
          cell.s = { ...NUMBER_STYLE }; // incluye numFmt y bordes
        }
      }
    }
  }

  // Autoajuste de anchos (en caracteres aprox.)
  const aoaAll = [headerRow1, headerRow2, ...rows];
  const colWidths = [];
  for (let c = 0; c < totalCols; c++) colWidths[c] = { wch: 10 }; // mínimo

  for (let r = 0; r < aoaAll.length; r++) {
    for (let c = 0; c < totalCols; c++) {
      const value = aoaAll[r][c];
      const str =
        typeof value === "number"
          ? value.toFixed(2) // así reservamos espacio para 2 decimales
          : (value ?? "").toString();
      colWidths[c].wch = Math.max(colWidths[c].wch, Math.min(60, str.length + 2));
    }
  }
  ws["!cols"] = colWidths;

  // Congelar encabezados (opcional)
  ws["!freeze"] = { xSplit: 0, ySplit: 2 };

  // Libro y descarga
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "FICHAS_DESCONTAMINACIÓN");

  const safeName = fileName?.endsWith(".xlsx") ? fileName : `${fileName || "FICHAS"}.xlsx`;
  const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = safeName;
  a.click();
  window.URL.revokeObjectURL(url);
};

export default exportSHEETToExcel;

